import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, RotateCcw, Trash2, Info, AlertTriangle, X } from 'lucide-react';
import { servers as api } from '../api';
import type { Snapshot } from '../api';

/**
 * Snapshots einer Maschine, über die Schnittstelle des Anbieters.
 *
 * ZWEI DINGE, DIE DIE OBERFLÄCHE SAGEN MUSS UND SONST NIEMAND SAGT:
 *
 *  1. **Warum es nicht geht**, wenn es nicht geht. Ein abgeblendeter Knopf
 *     lässt raten, und die Gründe sind hier nicht offensichtlich — dedizierte
 *     Hardware kann das gar nicht, eine nur eingetragene Maschine hat keine
 *     Kennung beim Anbieter, und ohne Zugang fehlt schlicht der Schlüssel.
 *     Der Text kommt vom Server, damit er an einer Stelle steht.
 *
 *  2. **Wen ein Zurücksetzen trifft.** Es überschreibt die ganze Platte, also
 *     alle Instanzen auf der Maschine — nicht die eine, wegen der man es tut.
 *     Deshalb die Zahl eintippen statt nur bestätigen.
 */

function zeitpunkt(wert: string): string {
  return new Date(wert).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function SnapshotPanel({
  serverId,
  hostname,
  onClose,
}: {
  serverId: string;
  hostname: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [beschreibung, setBeschreibung] = useState('');
  const [meldung, setMeldung] = useState('');
  const [fehler, setFehler] = useState('');
  const [zurueck, setZurueck] = useState<Snapshot | null>(null);
  const [bestaetigung, setBestaetigung] = useState('');

  const schluessel = ['servers', serverId, 'snapshots'];

  const { data, isLoading } = useQuery({
    queryKey: schluessel,
    queryFn: () => api.snapshots(serverId),
  });

  function beiFehler(vorgabe: string) {
    return (err: unknown) => {
      setFehler(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? vorgabe,
      );
      setMeldung('');
    };
  }

  const anlegen = useMutation({
    mutationFn: (ueberschreiben?: boolean) =>
      api.snapshotAnlegen(serverId, beschreibung, ueberschreiben),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: schluessel });
      setBeschreibung('');
      setFehler('');
      setMeldung([r.message, r.hinweis].filter(Boolean).join(' '));
    },
    onError: beiFehler('Anlegen fehlgeschlagen.'),
  });

  const loeschen = useMutation({
    mutationFn: (id: string) => api.snapshotLoeschen(serverId, id),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: schluessel });
      setFehler('');
      setMeldung(r.message);
    },
    onError: beiFehler('Löschen fehlgeschlagen.'),
  });

  const zuruecksetzen = useMutation({
    mutationFn: (id: string) => api.snapshotZuruecksetzen(serverId, id, data?.instanzen ?? 0),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: schluessel });
      setZurueck(null);
      setBestaetigung('');
      setFehler('');
      setMeldung(r.message);
    },
    onError: beiFehler('Zurücksetzen fehlgeschlagen.'),
  });

  const faehig = data?.faehigkeit;
  const instanzen = data?.instanzen ?? 0;
  const vorhanden = data?.snapshots ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4">
      <div className="mt-8 flex max-h-[88vh] w-full max-w-2xl flex-col overflow-y-auto rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">{t('snapshots.title')}</h2>
            <p className="font-mono text-xs text-slate-500">{hostname}</p>
          </div>
          <div className="flex items-center gap-2">
            {faehig?.moeglich && faehig.quelle && (
              <span className="text-xs text-slate-400">
                {faehig.quelle === 'customer'
                  ? t('snapshots.sourceCustomer')
                  : t('snapshots.sourceHouse')}
              </span>
            )}
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="px-6 py-4">
      <p className="text-xs text-slate-500 mb-3">{t('snapshots.explain')}</p>

      {fehler && <div className="text-xs text-red-600 mb-2">{fehler}</div>}
      {meldung && <div className="text-xs text-green-700 mb-2">{meldung}</div>}

      {isLoading ? (
        <div className="h-8 bg-slate-100 rounded animate-pulse" />
      ) : !faehig?.moeglich ? (
        // Der Grund im Klartext, nicht nur ein abgeblendeter Knopf.
        <div className="flex gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600">
          <Info size={14} className="text-slate-400 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-slate-700 mb-0.5">{t('snapshots.notPossible')}</p>
            <p>{faehig?.grund}</p>
          </div>
        </div>
      ) : (
        <>
          {faehig.nurEiner && (
            <p className="flex gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-2">
              <AlertTriangle size={13} className="shrink-0 mt-0.5" />
              {t('snapshots.onlyOneWarning')}
            </p>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              setFehler('');
              // Bei Hostinger liegt womöglich schon einer. Das Backend lehnt
              // dann ab; hier wird einmal nachgefragt und der Wille
              // ausdrücklich mitgeschickt.
              const ersetzen =
                faehig.nurEiner && vorhanden.length > 0
                  ? window.confirm(t('snapshots.overwriteConfirm'))
                  : false;
              if (faehig.nurEiner && vorhanden.length > 0 && !ersetzen) return;
              anlegen.mutate(ersetzen);
            }}
            className="flex gap-2 mb-3"
          >
            <input
              required
              maxLength={200}
              value={beschreibung}
              onChange={(e) => setBeschreibung(e.target.value)}
              placeholder={t('snapshots.descriptionPlaceholder')}
              className="flex-1 border border-slate-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <button
              type="submit"
              disabled={anlegen.isPending}
              className="inline-flex items-center gap-1 bg-brand-600 hover:bg-brand-700 text-white text-xs font-medium px-3 py-1.5 rounded disabled:opacity-60"
            >
              <Camera size={13} />
              {anlegen.isPending ? t('snapshots.creating') : t('snapshots.create')}
            </button>
          </form>

          {!vorhanden.length ? (
            <p className="text-xs text-slate-400 italic">{t('snapshots.none')}</p>
          ) : (
            <ul className="space-y-1.5">
              {vorhanden.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center justify-between gap-2 bg-surface border border-slate-200 rounded px-2.5 py-2 text-xs"
                >
                  <div className="min-w-0">
                    <p className="text-slate-700 truncate">{s.beschreibung || `#${s.id}`}</p>
                    <p className="text-slate-400">
                      {zeitpunkt(s.erstellt)}
                      {s.groesseGb != null && ` · ${t('snapshots.size', { gb: s.groesseGb })}`}
                      {s.laeuftAbAm &&
                        ` · ${t('snapshots.expires', { when: zeitpunkt(s.laeuftAbAm) })}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {faehig.zuruecksetzenMoeglich && (
                      <button
                        onClick={() => {
                          setZurueck(s);
                          setBestaetigung('');
                          setFehler('');
                        }}
                        className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-900"
                      >
                        <RotateCcw size={12} /> {t('snapshots.restore')}
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (window.confirm(t('snapshots.deleteConfirm'))) loeschen.mutate(s.id);
                      }}
                      className="inline-flex items-center gap-1 text-red-600 hover:text-red-800"
                    >
                      <Trash2 size={12} /> {t('snapshots.delete')}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

        </div>
      </div>

      {zurueck && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-surface rounded-lg shadow-xl max-w-md w-full p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-1">
              {t('snapshots.restoreTitle')}
            </h3>
            <p className="text-xs text-slate-500 mb-3 font-mono">
              {hostname} · {zurueck.beschreibung || `#${zurueck.id}`} ·{' '}
              {zeitpunkt(zurueck.erstellt)}
            </p>

            <p className="flex gap-1.5 text-xs text-red-800 bg-red-50 border border-red-200 rounded px-2.5 py-2 mb-3">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              {t('snapshots.restoreWarning', { count: instanzen })}
            </p>

            {/* Die Zahl eintippen, nicht nur klicken. Wer sie nicht kennt, hat
                nicht gelesen, wen es trifft — und genau das ist hier der
                teure Fehler. */}
            <label className="block text-xs text-slate-600 mb-1">
              {t('snapshots.restoreTypeToConfirm')}
            </label>
            <input
              autoFocus
              value={bestaetigung}
              onChange={(e) => setBestaetigung(e.target.value)}
              className="w-full border border-slate-300 rounded px-2 py-1.5 text-xs font-mono mb-3 focus:outline-none focus:ring-2 focus:ring-red-500"
            />

            {fehler && <div className="text-xs text-red-600 mb-2">{fehler}</div>}

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setZurueck(null);
                  setFehler('');
                }}
                className="text-xs text-slate-500 hover:text-slate-700 px-3 py-1.5"
              >
                {t('providerCredentials.cancel')}
              </button>
              <button
                disabled={bestaetigung.trim() !== String(instanzen) || zuruecksetzen.isPending}
                onClick={() => zuruecksetzen.mutate(zurueck.id)}
                className="bg-red-600 hover:bg-red-700 text-white text-xs font-medium px-3 py-1.5 rounded disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t('snapshots.restoreGo')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
